import { Query, Predicate } from "@syncfusion/ej2-data";
import { GetDataManager, GetDataManagerNew } from "../ds";
import GetOptions from "../common/form/options";
import GetOptionsPure from "../common/form/optionsPure";
import { getTranslation } from "../helpers/common";
import { apiGet } from "./apiHelper";
import {
  ORDER_STATUS,
  LINES,
  INCIDENT_ACTIONS_BATCH,
  INVOICE_TYPE,
  FAMILY_TYPE,
  ROLE_TYPE,
} from "../config/global";
import get from "lodash/get";

function getYesNoOptions(i18n) {
  return [
    { value: true, label: i18n.t("common.yesno.yes") },
    { value: false, label: i18n.t("common.yesno.no") },
  ];
}

function getYesNoOptionsReversed(i18n) {
  return [
    { value: false, label: i18n.t("common.yesno.yes") },
    { value: true, label: i18n.t("common.yesno.no") },
  ];
}

async function getMacroCategoryOptions(params) {
  const { lang } = params;
  const options = await GetOptionsPure({
    api: "structure_ptype",
    query_cache: "structure_ptype",
    cur_lang: lang,
    reducer: (map, obj) => {
      map.push({
        value: String(obj.id),
        label: obj.translations[lang].name,
      });
      return map;
    },
  });
  return options;
}

async function getParentCategoryOptions(params) {
  const { category__macrocategory_id, lang } = params;
  const options = await GetOptionsPure({
    api: "structure_category_browser",
    query: [
      {
        field: "parent_id",
        operator: "===",
        value: null,
      },
      {
        field: "macrocategory_id",
        operator: "===",
        value: parseInt(category__macrocategory_id),
      },
    ],
    query_cache: `structure_category_browser_M${category__macrocategory_id}`,
    cur_lang: lang,
  });
  return options;
}

async function getCategoryOptions(params) {
  const { category__macrocategory_id, category__parent_id, lang } = params;
  const options = await GetOptionsPure({
    api: "structure_category_browser",
    query: [
      {
        field: "parent_id",
        operator: "===",
        value: parseInt(category__parent_id),
      },
      {
        field: "macrocategory_id",
        operator: "===",
        value: parseInt(category__macrocategory_id),
      },
      {
        field: "parent_id",
        operator: ">",
        value: 0,
      },
    ],
    query_cache: `structure_category_browser_M${category__macrocategory_id}_P${category__parent_id}`,
    cur_lang: lang,
  });
  return options;
}

async function getAgeClassOptions(params) {
  const { lang } = params;
  const options = await GetOptionsPure({
    api: "structure_ageclass",
    query_cache: "structure_ageclass",
    cur_lang: lang,
  });
  return options;
}

async function getLogisticTypeOptions(params) {
  const { category__macrocategory_id, lang } = params;
  const query = [];
  if (category__macrocategory_id) {
    query.push({
      field: "macrocategory_id",
      operator: "===",
      value: category__macrocategory_id,
    });
  }
  const options = await GetOptionsPure({
    api: "structure_ltype",
    query: query,
    query_cache: `structure_ltype_${category__macrocategory_id || ""}`,
    reducer: params.reducer || undefined,
    cur_lang: lang,
  });
  return options;
}

async function getCertificationsOptions() {
  const options = await GetOptionsPure({
    api: "structure_certification",
    reducer_cache: "certifications_list",
    reducer: function (map, obj) {
      map.push({
        value: obj.id,
        label: obj.name,
        macro_id: obj.macrocategory_id,
      });
      return map;
    },
  });
  return options;
}

async function getCertificationsMacrocategoriesOptions() {
  const options = await GetOptionsPure({
    api: "structure_certification_macro",
    reducer_cache: "certifications_macro",
  });
  return options;
}

async function getProductionTypeOptions(params) {
  const { lang } = params;
  const options = await GetOptionsPure({
    api: "structure_production",
    query_cache: "structure_production",
    reducer_cache: "structure_production_need_nation",
    reducer: function (map, obj) {
      map.push({
        value: obj.id,
        label: obj.translations[lang].name,
        need_nation: obj.need_nation,
      });
      return map;
    },
  });
  return options;
}

async function getProductionTypeNationsOptions() {
  const options = await GetOptionsPure({
    api: "geo_country",
    reducer: function (map, obj) {
      map.push({
        value: obj.code,
        label: obj.name,
      });
      return map;
    },
    reducer_cache: "geo_country",
  });
  return options;
}

async function getTaxIdOptions(params) {
  const { country_id } = params;
  const options = await GetOptionsPure({
    api: "geo_tax",
    query: [
      {
        field: "country_id",
        value: country_id,
        operator: "===",
      },
    ],
    query_cache: "geo_tax_" + country_id,
  });
  return options;
}

async function getTaxRateOptions(params) {
  const { country_id } = params;
  const res = await apiGet("geo_tax", [country_id]);

  let results = [];
  if (res.data) {
    const items = res.data.Items;
    items.forEach((item) => {
      if (item.country_id === country_id) {
        results.push({ value: item.id, label: `${item.rate}%` });
      }
    });
  }

  return results;
}

function getPwOptions(i18n) {
  return [
    {
      value: 1,
      label: i18n.t("models.lot.pwoptions.o1.label"),
      singular: i18n.t("models.lot.pwoptions.o1.singular"),
      plural: i18n.t("models.lot.pwoptions.o1.plural"),
      short: i18n.t("models.lot.pwoptions.o1.short"),
    },
    {
      value: 2,
      label: i18n.t("models.lot.pwoptions.o2.label"),
      singular: i18n.t("models.lot.pwoptions.o2.singular"),
      plural: i18n.t("models.lot.pwoptions.o2.plural"),
      short: i18n.t("models.lot.pwoptions.o2.short"),
    },
    {
      value: 3,
      label: i18n.t("models.lot.pwoptions.o3.label"),
      singular: "",
      plural: "",
    },
  ];
}

function getLastMinTOptions(i18n) {
  return [
    {
      value: "d",
      label: i18n.t("models.productproducer.duration.days"),
    },
    {
      value: "m",
      label: i18n.t("models.productproducer.duration.months"),
    },
    {
      value: "y",
      label: i18n.t("models.productproducer.duration.years"),
    },
  ];
}

function getPriceUnitOptions(i18n) {
  return [
    { value: 1, label: "Kg" },
    { value: 2, label: "gr" },
    { value: 3, label: "L" },
    { value: 4, label: "dl" },
    { value: 5, label: "cl" },
    { value: 6, label: "ml" },
    { value: 7, label: i18n.t("models.lot.priceUnitOptions.o7") },
    { value: 8, label: i18n.t("models.lot.priceUnitOptions.o8") },
  ];
}

function getPriceUnitOptionsWeighed(i18n) {
  return [
    { value: 1, label: "Kg" },
    { value: 2, label: "gr" },
    { value: 3, label: "L" },
    { value: 4, label: "dl" },
    { value: 5, label: "cl" },
    { value: 6, label: "ml" },
  ];
}

function getAvailabilityLimitlessOptions(i18n) {
  return [
    {
      value: true,
      label: i18n.t("options.availabilityLimitless.limitless"),
    },
    {
      value: false,
      label: i18n.t("options.availabilityLimitless.limited"),
    },
  ];
}

function getAvailabilityXdOptions(i18n) {
  return [
    {
      value: true,
      label: i18n.t("options.availabilityXd.delivery"),
    },
    {
      value: false,
      label: i18n.t("options.availabilityXd.absolute"),
    },
  ];
}

function getValidLimitedOptions(i18n) {
  return [
    {
      value: "always",
      label: i18n.t("options.availabilityLimited.always"),
    },
    {
      value: "limited",
      label: i18n.t("options.availabilityLimited.limited"),
    },
  ];
}

function getIslandOptions() {
  return [
    {
      value: 1,
      label: "Aperta al pubblico",
    },
    {
      value: 2,
      label: "Privata",
    },
    {
      value: 3,
      label: "A domicilio",
    },
  ];
}

async function getProducerCategoryTree(params) {
  const { lang, roleId } = params;

  const response = await apiGet("role_producer_categorytree", [roleId]);
  const data = response.data.payload.Items;

  const categories = [];
  data.forEach((macro) => {
    const macroName = getTranslation(macro.translations, lang);
    macro.categories.forEach((parent) => {
      const parentName = getTranslation(parent.translations, lang);
      const optgroup = {
        label: `${macroName} - ${parentName}`,
        options: parent.children.map((c) => {
          return {
            value: c.id,
            label: getTranslation(c.translations, lang),
          };
        }),
      };
      categories.push(optgroup);
    });
  });
  return categories;
}

async function getProducerTypeProducts(params) {
  const { lang, roleId } = params;

  const response = await apiGet("role_producer_typeproduct", [roleId]);
  const data = response.data.payload.Items;

  const options = data.map((option) => {
    return {
      value: option.id,
      label: getTranslation(option.translations, lang),
    };
  });
  return options;
}

async function getMarketCategoryTree(params) {
  const { familyId, marketId, lang } = params;

  const optionsNew = await apiGet("family_market_categories", [
    familyId,
    marketId,
  ]);

  const categories = [];
  optionsNew.data.payload.Items.forEach((macro) => {
    const macroName = getTranslation(macro.translations, lang);
    macro.categories.forEach((parent) => {
      const parentName = getTranslation(parent.translations, lang);
      const optgroup = {
        label: `${macroName} - ${parentName}`,
        options: parent.children.map((c) => {
          return {
            value: c.id,
            label: getTranslation(c.translations, lang),
          };
        }),
      };
      categories.push(optgroup);
    });
  });
  return categories;
}

async function getMarketProducersOptions(params) {
  const { familyId, marketId } = params;

  const optionsNew = await apiGet("family_market_producers", [
    familyId,
    marketId,
  ]);

  const options = optionsNew.data.payload.Items.map((option) => {
    return {
      value: option.id,
      label: option.role.name,
    };
  });
  return options;
}

function productDietOptions(i18n) {
  return [
    {
      value: "gluten_free",
      label: i18n.t("models.productproducer.diet.gluten_free"),
    },
    {
      value: "vegan",
      label: i18n.t("models.productproducer.diet.vegan"),
    },
    {
      value: "vegetarian",
      label: i18n.t("models.productproducer.diet.veg"),
    },
    {
      value: "lactose_free",
      label: i18n.t("models.productproducer.diet.lactose_free"),
    },
  ];
}

async function getRoundtWarehouseOptions(params) {
  const { roleId, roundId } = params;
  const query = new Query();
  const options = await GetOptions({
    dm: GetDataManagerNew("role_market_orderswh", [roleId, roundId]),
    reducer: function (map, obj) {
      map.push({
        value: obj.id,
        label: obj.wh.address.to,
      });
      return map;
    },
    query: query,
    query_cache: `structure_round_warehouses_${roleId || ""}_${roundId || ""}`,
  });
  return options;
}

async function getRoundtLogisticTypeOptions(params) {
  const { roleId, roundId, lang } = params;
  const query = new Query();
  const options = await GetOptions({
    dm: GetDataManagerNew("role_market_orders_tp", [roleId, roundId]),
    reducer: function (map, obj) {
      map.push({
        value: obj.id,
        label: get(obj, `translations.${lang}.name`, ""),
      });
      return map;
    },
    query: query,
    query_cache: `structure_round_tp_${roleId || ""}_${roundId || ""}`,
  });
  return options;
}

function getOrderStatusOptions(i18n) {
  return [
    {
      value: ORDER_STATUS.CREATE,
      label: i18n.t("models.filters.orderStatus.confirmed"),
    },
    {
      value: ORDER_STATUS.PAYMENT_WAIT,
      label: i18n.t("models.filters.orderStatus.waitingForPayment"),
    },
    {
      value: ORDER_STATUS.APPROVAL,
      label: i18n.t("models.filters.orderStatus.approved"),
    },
    {
      value: ORDER_STATUS.CANCELED,
      label: i18n.t("models.filters.orderStatus.canceled"),
    },
    {
      value: ORDER_STATUS.ALERT,
      label: i18n.t("models.filters.orderStatus.reported"),
    },
    {
      value: ORDER_STATUS.PRODUCER_IN_CHARGE,
      label: i18n.t("models.filters.orderStatus.producer"),
    },
    {
      value: ORDER_STATUS.MARKET_ARRIVED,
      label: i18n.t("models.filters.orderStatus.deliveredToWh"),
    },
    {
      value: ORDER_STATUS.MARKET_STOCK,
      label: i18n.t("models.filters.orderStatus.inStockAtWh"),
    },
    {
      value: ORDER_STATUS.MARKET_IN_CHARGE,
      label: i18n.t("models.filters.orderStatus.ready"),
    },
    {
      value: ORDER_STATUS.ISLAND_ARRIVED,
      label: i18n.t("models.filters.orderStatus.readyForDelivery"),
    },
    {
      value: ORDER_STATUS.ISLAND_IN_CHARGE,
      label: i18n.t("models.filters.orderStatus.IslandInCharge"),
    },
    {
      value: ORDER_STATUS.FAMILY_IN_CHARGE,
      label: i18n.t("models.filters.orderStatus.familyInCharge"),
    },
    {
      value: ORDER_STATUS.DELIVERED,
      label: i18n.t("models.filters.orderStatus.delivered"),
    },
    {
      value: ORDER_STATUS.PAID,
      label: i18n.t("models.filters.orderStatus.paid"),
    },
    {
      value: ORDER_STATUS.ERROR,
      label: i18n.t("models.filters.orderStatus.error"),
    },
  ];
}

function getOrderLineOptions(i18n) {
  return [
    {
      value: LINES.OTHER,
      label: i18n.t("models.filters.LineOptions.other"),
    },
    {
      value: LINES.BOX,
      label: i18n.t("models.filters.LineOptions.box"),
    },
    {
      value: LINES.FRIDGE,
      label: i18n.t("models.filters.LineOptions.fridge"),
    },
    {
      value: LINES.SHIP,
      label: i18n.t("models.filters.LineOptions.shipment"),
    },
  ];
}
function getIncidentBatchActionOptions(i18n) {
  return [
    {
      value: INCIDENT_ACTIONS_BATCH.CANCELLED,
      label: i18n.t("role.orders.manage.incident.actions.cancel"),
    },
    {
      value: INCIDENT_ACTIONS_BATCH.CANCELLED_BY_PRODUCER,
      label: i18n.t(
        "role.orders.manage.incident.actions.cancelledAtProducersExpense"
      ),
    },
    {
      value: INCIDENT_ACTIONS_BATCH.CANCELLED_BY_MARKET,
      label: i18n.t(
        "role.orders.manage.incident.actions.cancelledAtMarketsExpense"
      ),
    },
    {
      value: INCIDENT_ACTIONS_BATCH.CANCELLED_BY_ISLAND,
      label: i18n.t(
        "role.orders.manage.incident.actions.cancelledAtIslandsExpense"
      ),
    },
    {
      value: INCIDENT_ACTIONS_BATCH.REMOVE_FROM_INCIDENT,
      label: i18n.t("role.orders.manage.incident.actions.removeFromIncident"),
    },
  ];
}

function getInvoiceTypeOptions(i18n) {
  return [
    {
      value: INVOICE_TYPE.CUSTOMER,
      label: i18n.t(
        "role.finance.invoicesOut.filters.search.type.dropdown.client"
      ),
    },
    {
      value: INVOICE_TYPE.MARKET,
      label: i18n.t(
        "role.finance.invoicesOut.filters.search.type.dropdown.market"
      ),
    },
    {
      value: INVOICE_TYPE.PRODUCER,
      label: i18n.t(
        "role.finance.invoicesOut.filters.search.type.dropdown.producer"
      ),
    },
    {
      value: INVOICE_TYPE.ISLAND,
      label: i18n.t(
        "role.finance.invoicesOut.filters.search.type.dropdown.island"
      ),
    },
  ];
}

function getFamilyTypeOptions(app) {
  return [
    { value: FAMILY_TYPE.PRIVATE, label: app.$t("options.familytype.private") },
    { value: FAMILY_TYPE.SOCIETY, label: app.$t("options.familytype.society") },
    { value: FAMILY_TYPE.COOP, label: app.$t("options.familytype.coop") },
  ];
}

function getRoleTypeOptions(app) {
  return [
    { value: ROLE_TYPE.MARKET, label: app.$t("options.roletype.market") },
    { value: ROLE_TYPE.PRODUCER, label: app.$t("options.roletype.producer") },
    {
      value: ROLE_TYPE.PRODUCERISLAND,
      label: app.$t("options.roletype.producerisland"),
    },
    { value: ROLE_TYPE.ISLAND, label: app.$t("options.roletype.island") },
    { value: ROLE_TYPE.SHOP, label: app.$t("options.roletype.shop") },
  ];
}

async function getNationsActiveOptions() {
  const options = await GetOptions({
    dm: GetDataManager("geo_country"),
    query: new Query().where("status", "equal", 100),
    reducer: function (map, obj) {
      map.push({ value: obj.code, label: obj.name });
      return map;
    },
    reducer_cache: "geo_country_active",
  });
  return options;
}

async function getPostalCodeOptions(params) {
  const { countryId, search } = params;

  if (countryId) {
    let predicate = new Predicate("country_id", "equal", countryId);
    if (search) {
      predicate = predicate.and("code", "contains", search);
    }
    const query = new Query().where(predicate).take(10);
    const options = await GetOptions({
      dm: GetDataManager("geo_postalcode"),
      query: query,
      reducer: function (map, obj) {
        //map.push({ value: obj.id, label: obj.code });
        map.push(obj);
        return map;
      },
      reducer_cache: `structure_postalcode_${countryId}_${search || ""}`,
    });
    return options;
  }
  return [];
}

async function getAddressSearchOptions(params) {
  const { search, market_id } = params;
  if (search) {
    let predicate = new Predicate("email", "contains", search);

    const query = new Query().where(predicate).take(10);
    const options = await GetOptions({
      dm: GetDataManager("role_market_address_search", [market_id]),
      query: query,
      reducer: function (map, obj) {
        map.push({
          value: obj.email,
          label: obj.email,
          count: obj.count,
          families: obj.families,
        });
        return map;
      },
      reducer_cache: `structure_address_search_${search || ""}`,
    });
    return options;
  }
  return [];
}

async function getAddFormPpmSearchOptions(params) {
  const { search, market_id, producer_id } = params;
  if (search) {
    let predicate = new Predicate(
      "ppl__productproducer__translations__name",
      "contains",
      search
    );

    // "ppl__productproducer__name", "ppl__name"
    const query = new Query().where(predicate).take(10);
    const options = await GetOptions({
      dm: GetDataManager("role_producer_orders_add_products", [
        producer_id,
        market_id,
      ]),
      query: query,
      reducer: function (map, obj) {
        //console.log("ciao", map, obj);
        map.push({
          value: obj,
          label:
            obj.ppl.productproducer.translations.it.name +
            " - " +
            obj.ppl.translations.it.name,
        });
        return map;
      },
      // reducer_cache: `role_producer_orders_add_products_${search || ""}`,
    });
    return options;
  }
  return [];
}

async function getProducerMarkets(params, app) {
  const { producer_id } = params;
  // console.log("get markets", producer_id)

  const options = await GetOptionsPure({
    api: "role_producer_markets",
    params: [producer_id],
    reducer: function (map, obj) {
      let name = obj.name;
      if (obj.connected == true) {
        name = "(C) " + name;
      }
      map.push({
        value: obj.id,
        label: name,
        connected: obj.connected,
      });
      return map;
    },
  });

  const cm = [];
  const ncm = [];
  options.forEach((opt) => {
    if (opt.connected) {
      cm.push(opt);
    } else {
      ncm.push(opt);
    }
  });
  let ooptions = [...cm, ...ncm];
  ooptions.unshift({
    value: null,
    label: app.$t("role.orders.rounds.filters.allOption"),
  });
  return ooptions;
}

export {
  getYesNoOptions,
  getYesNoOptionsReversed,
  getMacroCategoryOptions,
  getParentCategoryOptions,
  getCategoryOptions,
  getAgeClassOptions,
  getLogisticTypeOptions,
  getCertificationsOptions,
  getCertificationsMacrocategoriesOptions,
  getProductionTypeOptions,
  getProductionTypeNationsOptions,
  getTaxIdOptions,
  getTaxRateOptions,
  getPwOptions,
  getLastMinTOptions,
  getPriceUnitOptions,
  getPriceUnitOptionsWeighed,
  getAvailabilityLimitlessOptions,
  getAvailabilityXdOptions,
  getValidLimitedOptions,
  getIslandOptions,
  getProducerCategoryTree,
  getProducerTypeProducts,
  getMarketCategoryTree,
  getMarketProducersOptions,
  productDietOptions,
  getRoundtWarehouseOptions,
  getRoundtLogisticTypeOptions,
  getOrderStatusOptions,
  getOrderLineOptions,
  getIncidentBatchActionOptions,
  getInvoiceTypeOptions,
  getFamilyTypeOptions,
  getRoleTypeOptions,
  getNationsActiveOptions,
  getPostalCodeOptions,
  getAddressSearchOptions,
  getAddFormPpmSearchOptions,
  getProducerMarkets,
};
